import {IHttpService, ILogService, IPromise, IQService} from 'angular';
import {TSRole} from '../../models/enums/TSRole';
import TSAnlaufstelle from '../../models/TSAnlaufstelle';
import TSBenutzer from '../../models/TSBenutzer';
import TSInspektorat from '../../models/TSInspektorat';
import TSPasswordData from '../../models/TSPasswordData';
import IRootScopeService = angular.IRootScopeService;

/**
 * Service zum verwalten von Benutzerdaten. Beinhaltet KEINE Informationen ueber den aktuellen eingeloggten Benutzer
 * Dafuer ist der AuthServiceRS zustaendig. Auch um Logins oder Logouts durchzufuehren ist der AuthService zu gebrauchen
 */
export default class BenutzerRS {

    static $inject = ['$http', 'CONSTANTS', '$q', '$rootScope', '$log'];

    /* @ngInject */
    constructor(private $http: IHttpService, private CONSTANTS: any, private $q: IQService,
                private $rootScope: IRootScopeService, private $log: ILogService) {
    }

    public createAndSendPasswordResetToken(userEmail?: string): IPromise<void> {
        if (userEmail) {
            return this.$http.post(this.CONSTANTS.REST_API + 'benutzer/public/passwordreset', userEmail, {
                headers: {
                    'Content-Type': 'text/plain'
                }
            }).then((response) => {
                return this.$q.defer().resolve();  //void promise zurueckgeben
            }).catch(() => {
                return this.$q.defer().resolve();
            });

        }
        this.$log.error('passed no email, no reset request sent');
        return this.$q.when(undefined);
    };

    public resetPassword(pwData?: TSPasswordData): IPromise<void> {
        if (pwData) {
            return this.$http.post(this.CONSTANTS.REST_API + 'benutzer/public/changepw', pwData.serialize({}))
                .then((response) => {
                    return this.$q.defer().resolve();
                });

        }
        this.$log.error('passed no data, no reset request sent');
        return this.$q.when(undefined);
    };

    public possibleVerantwortliche(): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/verantworltiche/possible')
            .then((response: any) => {
                let converter = (jsonString: any) => {
                    return new TSBenutzer().deserialize(jsonString);
                };
                if (angular.isArray(response.data)) {
                    return response.data.map(converter);
                }

            });
    }

    public possibleZahlungsauftragBesitzer(): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/zahlungsauftrag/possible')
            .then((response: any) => {
                let converter = (jsonString: any) => {
                    return new TSBenutzer().deserialize(jsonString);
                };
                if (angular.isArray(response.data)) {
                    return response.data.map(converter);
                }

            });
    }

    public allBenutzerForRole(role: TSRole): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/alle/' + role)
            .then((response: any) => {
                let converter = (jsonString: any) => {
                    return new TSBenutzer().deserialize(jsonString);
                };
                if (angular.isArray(response.data)) {
                    return response.data.map(converter);
                }

            });

    }

    public createNewBasicBenutzeraccount(benutzer?: TSBenutzer): IPromise<TSBenutzer | undefined> {
        if (benutzer) {
            let restBenutzer: any = benutzer.serialize({});
            return this.$http.put(this.CONSTANTS.REST_API + 'benutzer/public/basicAccount', benutzer.serialize({}))
                .then((response) => {
                    if (response.data !== undefined) {
                        return new TSBenutzer().deserialize(response.data);
                    } else {
                        return undefined;
                    }
                });

        }
        this.$log.error('passed no data, no reset request sent');
        return this.$q.when(undefined);
    }

    public createOrUpdateBenutzeraccount(benutzer?: TSBenutzer): IPromise<TSBenutzer | undefined> {
        if (benutzer) {
            let restBenutzer: any = benutzer.serialize({});
            //prepare callback
            let successCallback = (response: any) => {
                if (response.data !== undefined) {
                    return new TSBenutzer().deserialize(response.data);
                } else {
                    return undefined;
                }
            };
            //call persist or merge methods
            if (benutzer.isNew()) {
                return this.$http.post(this.CONSTANTS.REST_API + 'benutzer/advancedAccount', benutzer.serialize({}))
                    .then(successCallback);
            } else {
                //update
                return this.$http.put(this.CONSTANTS.REST_API + 'benutzer/advancedAccount', benutzer.serialize({}))
                    .then(successCallback);
            }
        }
        this.$log.error('passed no data, no reset request sent');
        return this.$q.when(undefined);
    }

    public updateOwnUser(benutzer?: TSBenutzer, tokenLogin: boolean = false): IPromise<TSBenutzer | undefined> {
        if (benutzer) {
            let restBenutzer: any = benutzer.serialize({});
            let url = this.CONSTANTS.REST_API + 'benutzer/ownAccount';
            url = url + (tokenLogin ? '/token' : '');
            //call persist or merge methods
            if (!benutzer.isNew() || benutzer.passwordData && benutzer.passwordData.resetToken) {
                //update

                return this.$http.put(url, benutzer.serialize({}))
                    .then(response => {
                        if (response.data !== undefined) {
                            return new TSBenutzer().deserialize(response.data);
                        } else {
                            return undefined;
                        }
                    });
            } else {
                this.$log.error('user must not be new or rest token must be set');
            }
        }
        this.$log.error('passed no updatable data, no reset request sent');
        return this.$q.when(undefined);
    }

    public verify(benutzerid: string): IPromise<TSBenutzer | undefined> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/public/verify/' + encodeURIComponent(benutzerid))
            .then((response) => {
                if (response.data !== undefined) {
                    return new TSBenutzer().deserialize(response.data);
                } else {
                    return undefined;
                }
            })
    }

    public getAnlaufstellenForBenutzer(benutzerId: string): IPromise<Array<TSAnlaufstelle>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/' + encodeURIComponent(benutzerId) + '/anlaufstellen')
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Anlaufstellen REST  object', response.data);
                    return TSAnlaufstelle.deserializeArray(response.data);
                } else {
                    return [];
                }
            })
    }

    public getAnlaufstellenForSachbearbeiter(benutzerId: string): IPromise<Array<TSAnlaufstelle>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/' + encodeURIComponent(benutzerId) + '/sachbearbeiter/anlaufstellen')
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Anlaufstellen REST  object', response.data);
                    return TSAnlaufstelle.deserializeArray(response.data);
                } else {
                    return this.$q.when<Array<TSAnlaufstelle>>([]);
                }
            })
    }

    public getInspektoratForBenutzer(benutzerId: string): IPromise<Array<TSInspektorat>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/' + encodeURIComponent(benutzerId) + '/inspektorat')
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Anlaufstellen REST  object', response.data);
                    return TSInspektorat.deserializeArray(response.data);
                } else {
                    return [];
                }
            })
    }

    public readBenutzerSuggestion(searchString: string): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/search',
            {
                params: {
                    queryString: searchString,
                    rolesToExclude: [TSRole[TSRole.BETROFFENER], TSRole[TSRole.SUPER_ADMIN]]
                }
            })
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Benutzer REST  object', response.data);
                    return TSBenutzer.deserializeArray(response.data);
                } else {
                    return [];
                }
            })
    }

    public loadAdvancedBenutzer(): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/list',
            {
                params: {}
            })
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Benutzer REST  object', response.data);
                    return TSBenutzer.deserializeArray(response.data);
                } else {
                    return [];
                }
            })
    }

    public findBenutzerInRole(rolesToSearch: Array<string>, rolesToExclude: Array<string>): IPromise<Array<TSBenutzer>> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/listusers',
            {
                params: {
                    rolesToFind: rolesToSearch,
                    rolesToExclude: rolesToExclude,
                    allowInactive: false    //keine inaktiven
                }
            })
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Benutzer REST  object', response.data);
                    return TSBenutzer.deserializeArray(response.data);
                } else {
                    return [];
                }
            })
    }

    public findBenutzer(benutzerId: string): IPromise<TSBenutzer | undefined> {
        return this.$http.get(this.CONSTANTS.REST_API + 'benutzer/user/' + encodeURIComponent(benutzerId),
            {
                params: {}
            })
            .then((response) => {
                if (response.data !== undefined) {
                    this.$log.debug('PARSING Benutzer REST  object', response.data);
                    return new TSBenutzer().deserialize(response.data);
                } else {
                    return undefined;
                }
            })
    }

    public inviteAdvancedBenutzer(userEmail: string | undefined, anlaufstelleId: string, role: TSRole): IPromise<TSAnlaufstelle | undefined> {
        if (!userEmail) {
            console.log('userEmail must be given');
            return this.$q.when(undefined);
        }
        const url = this.CONSTANTS.REST_API + 'benutzer/' + encodeURIComponent(userEmail) + '/anlaufstelle/' + encodeURI(anlaufstelleId);
        return this.$http.post(url, role, {
            headers: {
                'Content-Type': 'text/plain'
            }
        }).then((response) => {
            console.log('invite success ', response);
            return new TSAnlaufstelle().deserialize(response.data);
        })
    }

    public resetUserRoles(userEmail: string | undefined): IPromise<void> {
        if (userEmail) {
            return this.$http.delete(this.CONSTANTS.REST_API + 'benutzer/resetRoles/' + encodeURIComponent(userEmail))
                .then((response) => {
                    return this.$q.defer().resolve();
                });
        }
        this.$log.error('passed no data, no reset request sent');
        return this.$q.when(undefined);
    }

    public removeBetroffeneRole(userEmail: string | undefined): IPromise<void> {
        if (userEmail) {
            return this.$http.delete(this.CONSTANTS.REST_API + 'benutzer/removeBetroffeneRole/' + encodeURIComponent(userEmail))
                .then((response) => {
                    return this.$q.defer().resolve();
                });
        }
        this.$log.error('passed no data, no reset request sent');
        return this.$q.when(undefined);
    }
}



// WEBPACK FOOTER //
// ./src/app/core/service/BenutzerRS.rest.ts